import { LoadingButton } from '@mui/lab';
import { Tooltip } from '@mui/material';
import useRoles from 'common/hooks/useRoles';
import { EnableDispenseFlow } from 'common/utils';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateLineItemStatus } from 'store/thunks/patientThunks';

import { ItemType } from 'enums/common.enum';
import lineItemStatus from 'enums/lineItem.enum';
import orderEnum from 'enums/order.enum';
import FillProductLotDialog from './FillProductLotDialog';

const PatientFillProductButton = ({
  item,
  orderId,
  mpi,
  isProcessedExternal,
  orderStatus,
  fulfillingPharmacyId,
  isEnriching,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [isButtonLoading, setButtonLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [toolTipMsg, setToolTipMsg] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const hasAccess = useRoles();

  // POM-5873: OTC items live in otc_catalog, which carries no catalog productId, and
  // inventory is keyed by product_id — so an OTC line item can never have lots to pick.
  // Skip the lot dialog and fulfil directly, which is the same call the pre-dispense-flow
  // build makes today.
  const isLotSelectionEnabled = EnableDispenseFlow && item.itemType !== ItemType.OTC;

  const disabled =
    isEnriching ||
    item.lineItemStatus !== lineItemStatus.PRODUCT_FULFILLMENT ||
    item.lineItemStatus === lineItemStatus.RE_PROCESS ||
    !hasAccess.manageProductFulfillmentStatus ||
    item.taggedForReview ||
    item.processingState === 'FILL_LATER' ||
    orderStatus === orderEnum.EXPIRED ||
    orderStatus === orderEnum.FILL_LATER;

  useEffect(() => {
    if (isEnriching) {
      setToolTipMsg('Loading item details…');
    } else if (orderStatus === orderEnum.EXPIRED) {
      setToolTipMsg('Order has expired');
    } else if (orderStatus === orderEnum.FILL_LATER || item.processingState === 'FILL_LATER') {
      setToolTipMsg('Item is on Fill Later hold until its scheduled date');
    } else if (isProcessedExternal) {
      setToolTipMsg('Item processing is external');
    } else if (item.lineItemStatus === lineItemStatus.CANCELED) {
      setToolTipMsg('Item is Cancelled');
    } else if (item.lineItemStatus === lineItemStatus.RE_PROCESS) {
      setToolTipMsg('Item is in the reprocessing state');
    } else if (item.reviewReason && item.reviewReason === 'COMPOUNDING_REVIEW') {
      setToolTipMsg(
        'Item is under a compounding review; fulfillment is blocked until the compounding review is complete.'
      );
    } else if (item.taggedForReview) {
      setToolTipMsg('Item tagged for Review');
    } else {
      setToolTipMsg('');
    }
  }, [item, isProcessedExternal, orderStatus, isEnriching]);

  const handleClick = () => {
    if (isLotSelectionEnabled) {
      setDialogOpen(true);
    } else {
      setButtonLoading(true);
      dispatch(
        updateLineItemStatus({
          lineItemId: item.lineItemId,
          mpi,
          orderId,
          state: 'PRODUCT_FULFILLED',
        })
      )
        // updateLineItemStatus swallows failures (it dispatches handleError and resolves
        // undefined), so the success snackbar has to be gated on its resolved value — otherwise
        // a rejected fill reports "Fulfilled" alongside the error dialog.
        .then((isFulfilled) => {
          if (!isFulfilled) return undefined;
          enqueueSnackbar('Product successfully Fulfilled', { variant: 'success' });
          return onSuccess?.();
        })
        .finally(() => {
          setButtonLoading(false);
        });
    }
  };

  const handleConfirm = ({ selectedLots = [], unassigned = false } = {}) => {
    setIsConfirming(true);
    const lotAllocations =
      unassigned || selectedLots.length === 0
        ? undefined
        : selectedLots.map(
            ({
              inventoryLotId,
              lotNumber,
              ndc,
              qtyToAllocate,
              isNewLot,
              expirationDate,
              suppliesBud,
              recordedManually,
              fillQuantity,
              allowShortfallAsUnverified,
            }) => ({
              // POM-6083 — null for a manually recorded lot. The BE resolves it against the
              // UNFILTERED lot table and creates the row only if nothing matches, so a lot the
              // picker hid (fully dispensed, so filtered out) is claimed rather than duplicated.
              inventoryLotId: inventoryLotId ?? null,
              // POM-6083 — authoritative provenance: this row came from the manual panel. The
              // API needs it for two decisions the quantity alone cannot answer: only a
              // hand-recorded row may post an unverified claim (a picker-offered lot drained by
              // a concurrent fill must still fail INSUFFICIENT), and only a hand-recorded row is
              // exempt from the single-fill dispensability gate.
              ...(recordedManually ? { isManualEntry: true } : {}),
              lotNumber,
              ndc,
              // POM-6147 - for a lot that cannot cover the fill, `fillQuantity` is the whole
              // prescription quantity and the API splits it: what the lot holds comes out of
              // remaining_qty, the difference posts to unverified_qty on the same lot. Absent on
              // every other row, where the allocated quantity IS the quantity.
              quantity: Number.parseFloat(fillQuantity ?? qtyToAllocate) || 0,
              // Permission, not instruction - the API only splits when the lot is actually
              // short. Sent solely from a screen that has told the operator what happens to the
              // remainder, so a genuine INSUFFICIENT_LOT_QUANTITY is never masked as a claim.
              ...(allowShortfallAsUnverified ? { allowShortfallAsUnverified: true } : {}),
              // Only sent for manual rows, so a normal picked lot's payload is unchanged.
              ...(isNewLot ? { isNewLot: true, expirationDate: expirationDate ?? null } : {}),
              // POM-6083 — a BUD alongside a REAL lot id, which the API applies to that lot.
              // Sent only when the lot had none: it is ignored for a new lot (the id is null,
              // so expirationDate above already carries it) and refused for a dated one.
              ...(!isNewLot && suppliesBud ? { expirationDate: expirationDate ?? null } : {}),
            })
          );
    dispatch(
      updateLineItemStatus({
        lineItemId: item.lineItemId,
        mpi,
        orderId,
        state: 'PRODUCT_FULFILLED',
        lotAllocations,
        // POM-5933: an unassigned fill and "Proceed with Existing" both send no lotAllocations.
        // Without this flag the BE cannot tell them apart and reconciles the unassigned fill
        // against the create-time reservation — which is the expired lot that forced the
        // unassigned fill in the first place, so the fill is rejected.
        unassignedFill: unassigned,
      })
    )
      // Gated on the resolved value: updateLineItemStatus swallows failures and resolves
      // undefined, so an unguarded .then() reports success on a rejected fill.
      .then((isFulfilled) => {
        if (!isFulfilled) return undefined;
        setDialogOpen(false);
        enqueueSnackbar('Product successfully Fulfilled', { variant: 'success' });
        return onSuccess?.();
      })
      .finally(() => {
        setIsConfirming(false);
      });
  };

  return (
    <>
      <Tooltip title={toolTipMsg} placement='top' arrow>
        <span>
          <LoadingButton
            variant='contained'
            onClick={handleClick}
            sx={{ minWidth: '100px' }}
            loading={isButtonLoading}
            disabled={disabled || isProcessedExternal}
          >
            {item.lineItemStatus === 'PRODUCT_FULFILLED' ? 'Fulfilled' : 'Fill Product'}
          </LoadingButton>
        </span>
      </Tooltip>

      {isLotSelectionEnabled && (
        <FillProductLotDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleConfirm}
          item={item}
          fulfillingPharmacyId={fulfillingPharmacyId}
          mpi={mpi}
          isConfirming={isConfirming}
        />
      )}
    </>
  );
};

PatientFillProductButton.propTypes = {
  item: PropTypes.shape({
    itemName: PropTypes.string,
    itemType: PropTypes.string,
    ndc: PropTypes.string,
    productId: PropTypes.string,
    rxNumber: PropTypes.string,
    strength: PropTypes.string,
    form: PropTypes.string,
    quantity: PropTypes.string,
    lineItemStatus: PropTypes.string,
    processingState: PropTypes.string,
    lastUpdated: PropTypes.string,
    lineItemId: PropTypes.string,
    taggedForReview: PropTypes.bool,
    reviewReason: PropTypes.string,
  }).isRequired,
  orderId: PropTypes.string.isRequired,
  isProcessedExternal: PropTypes.bool.isRequired,
  mpi: PropTypes.string.isRequired,
  fulfillingPharmacyId: PropTypes.string.isRequired,
  orderStatus: PropTypes.string,
  isEnriching: PropTypes.bool,
  onSuccess: PropTypes.func,
};

PatientFillProductButton.defaultProps = {
  orderStatus: undefined,
  isEnriching: false,
  onSuccess: undefined,
};

export default PatientFillProductButton;
